import React, { useState, useEffect } from 'react';
import {
  Row, Col, Spin, Alert, Table, Tag, Space, Progress,
  Button, Select, Modal, Form, Input, DatePicker, Empty,
  Tabs, message, Statistic, Card, Tooltip, Badge, InputNumber
} from 'antd';
import {
  TeamOutlined, TrophyOutlined, SafetyCertificateOutlined, WarningOutlined,
  PlusOutlined, CheckCircleOutlined, ClockCircleOutlined, DollarOutlined,
  PercentageOutlined, ReloadOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, FileDoneOutlined, RiseOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { diversityService, clientVendorService } from '../services/api';
import dayjs from 'dayjs';
import '../styles/Phase5.css';

const { Option } = Select;

const SupplierDiversity = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [diverseVendors, setDiverseVendors] = useState([]);
  const [goals, setGoals] = useState([]);
  const [trend, setTrend] = useState(null);
  const [certificationTypes, setCertificationTypes] = useState([]);
  const [clientVendors, setClientVendors] = useState([]);

  const [certModalVisible, setCertModalVisible] = useState(false);
  const [goalModalVisible, setGoalModalVisible] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [certForm] = Form.useForm();
  const [goalForm] = Form.useForm();

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardData, certsData, vendorsData, goalsData, trendData, typesData, cvData] = await Promise.all([
        diversityService.getDashboard(currentYear).catch(() => null),
        diversityService.getCertifications({ limit: 100 }).catch(() => []),
        diversityService.getDiverseVendors({ limit: 100 }).catch(() => []),
        diversityService.getGoals({ fiscal_year: currentYear, status: 'active' }).catch(() => []),
        diversityService.getTrend(12).catch(() => null),
        diversityService.getCertificationTypes().catch(() => ({ types: [] })),
        clientVendorService.getAll().catch(() => []),
      ]);

      setDashboard(dashboardData);
      setCertifications(certsData || []);
      setDiverseVendors(vendorsData || []);
      setGoals(goalsData || []);
      setTrend(trendData);
      setCertificationTypes(typesData?.types || []);
      setClientVendors(cvData || []);

    } catch (err) {
      console.error('Error fetching diversity data:', err);
      setError('Failed to load diversity data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCertification = async (values) => {
    setActionLoading(true);
    try {
      await diversityService.createCertification({
        ...values,
        certification_date: values.certification_date?.toISOString(),
        expiration_date: values.expiration_date?.toISOString(),
      });
      message.success('Certification added successfully');
      setCertModalVisible(false);
      certForm.resetFields();
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Failed to add certification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyCertification = async (certId) => {
    setActionLoading(true);
    try {
      await diversityService.verifyCertification(certId);
      message.success('Certification verified');
      fetchData();
    } catch (err) {
      message.error('Failed to verify certification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCertification = async (certId) => {
    Modal.confirm({
      title: 'Remove Certification',
      content: 'Are you sure you want to remove this certification?',
      onOk: async () => {
        try {
          await diversityService.deleteCertification(certId);
          message.success('Certification removed');
          fetchData();
        } catch (err) {
          message.error('Failed to remove certification');
        }
      },
    });
  };

  const handleCreateGoal = async (values) => {
    setActionLoading(true);
    try {
      await diversityService.createGoal({
        ...values,
        fiscal_year: values.fiscal_year || currentYear,
      });
      message.success('Goal created successfully');
      setGoalModalVisible(false);
      goalForm.resetFields();
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Failed to create goal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecalculateSpend = async () => {
    setActionLoading(true);
    try {
      const result = await diversityService.calculateSpend({
        year: currentYear,
        month: new Date().getMonth() + 1,
      });
      message.success(`Spend calculated: ${result.diverse_percentage?.toFixed(1)}% diverse`);
      fetchData();
    } catch (err) {
      message.error('Failed to calculate spend');
    } finally {
      setActionLoading(false);
    }
  };

  // Chart: Diversity by Type
  const getDiversityByTypeChart = () => {
    if (!dashboard?.by_type?.length) return {};

    const data = dashboard.by_type.map(t => ({
      name: t.display_name.replace(' Business Enterprise', '').replace(' Enterprise', ''),
      value: t.total_spend
    })).filter(d => d.value > 0);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => `${params.name}: $${params.value?.toLocaleString()} (${params.percent}%)`
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: { color: '#64748b', fontSize: 11 }
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: { show: false },
        emphasis: {
          label: { show: true, fontSize: 14, fontWeight: 'bold' }
        },
        labelLine: { show: false },
        data: data,
        color: ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#ec4899']
      }]
    };
  };

  // Chart: Trend over time
  const getTrendChart = () => {
    if (!trend?.data_points?.length) return {};

    const dates = trend.data_points.map(p => p.date);
    const percentages = trend.data_points.map(p => p.diverse_percentage);

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => `${params[0].axisValue}: ${params[0].value?.toFixed(1)}%`
      },
      grid: { left: '3%', right: '4%', bottom: '10%', top: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: dates,
        axisLine: { lineStyle: { color: '#e2e8f0' } },
        axisLabel: { color: '#64748b', fontSize: 11, rotate: 45 }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: '#64748b', formatter: '{value}%' },
        splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } }
      },
      series: [{
        name: 'Diverse Spend %',
        type: 'line',
        data: percentages,
        smooth: true,
        lineStyle: { color: '#f59e0b', width: 3 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(245, 158, 11, 0.3)' },
              { offset: 1, color: 'rgba(245, 158, 11, 0.05)' }
            ]
          }
        },
        itemStyle: { color: '#f59e0b' }
      }]
    };
  };

  const certColumns = [
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text || 'Unknown'}</span>
    },
    {
      title: 'Certification',
      dataIndex: 'certification_type',
      key: 'certification_type',
      render: (type) => {
        const typeInfo = certificationTypes.find(t => t.code === type);
        return (
          <Tooltip title={typeInfo?.description}>
            <Tag color="blue">{type}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'Agency',
      dataIndex: 'certifying_agency',
      key: 'certifying_agency',
    },
    {
      title: 'Expiration',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (date) => {
        if (!date) return '-';
        const exp = dayjs(date);
        const daysUntil = exp.diff(dayjs(), 'day');
        let color = 'default';
        if (daysUntil < 0) color = 'red';
        else if (daysUntil < 30) color = 'orange';
        else if (daysUntil < 90) color = 'gold';
        return <Tag color={color}>{exp.format('MMM D, YYYY')}</Tag>;
      }
    },
    {
      title: 'Verified',
      dataIndex: 'is_verified',
      key: 'is_verified',
      render: (verified) => verified
        ? <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag>
        : <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {!record.is_verified && (
            <Tooltip title="Verify">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => handleVerifyCertification(record.id)}
              />
            </Tooltip>
          )}
          <Tooltip title="Remove">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCertification(record.id)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const vendorColumns = [
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: 'Annual Spend',
      dataIndex: 'annual_spend',
      key: 'annual_spend',
      render: (val) => <span className="p5-font-mono">${val?.toLocaleString()}</span>
    },
    {
      title: 'Certifications',
      dataIndex: 'certifications',
      key: 'certifications',
      render: (certs) => (
        <Space wrap>
          {certs?.map(c => <Tag key={c} color="blue">{c}</Tag>)}
        </Space>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space>
          {record.is_verified
            ? <Tag color="success">Verified</Tag>
            : <Tag color="warning">Unverified</Tag>
          }
          {record.expiring_soon && <Tag color="orange">Expiring Soon</Tag>}
        </Space>
      )
    }
  ];

  const goalColumns = [
    {
      title: 'Goal',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
            FY {record.fiscal_year} - {record.diversity_type || 'Total Diverse'}
          </div>
        </div>
      )
    },
    {
      title: 'Target',
      dataIndex: 'target_percentage',
      key: 'target_percentage',
      render: (val) => <span className="p5-font-mono">{val}%</span>
    },
    {
      title: 'Current',
      dataIndex: 'current_percentage',
      key: 'current_percentage',
      render: (val) => <span className="p5-font-mono">{val?.toFixed(1)}%</span>
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const progress = record.progress_percentage || 0;
        let status = 'normal';
        if (progress >= 100) status = 'success';
        else if (progress < 50) status = 'exception';
        return <Progress percent={Math.min(progress, 100).toFixed(0)} size="small" status={status} />;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = { achieved: 'green', active: 'blue', archived: 'default' };
        return <Tag color={colors[status]}>{status?.toUpperCase()}</Tag>;
      }
    }
  ];

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span className="p5-flex p5-gap-sm">
          <TeamOutlined />
          Overview
        </span>
      ),
      children: (
        <>
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <div className="p5-chart-container p5-animate-fadeUp">
                <div className="p5-chart-title">
                  <span><SafetyCertificateOutlined style={{ color: 'var(--p5-amber-500)', marginRight: '8px' }} />Diverse Spend by Category</span>
                </div>
                {dashboard?.by_type?.length > 0 ? (
                  <ReactECharts option={getDiversityByTypeChart()} style={{ height: 350 }} />
                ) : (
                  <Empty description="No diversity data available" />
                )}
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className="p5-chart-container p5-animate-fadeUp" style={{ animationDelay: '100ms' }}>
                <div className="p5-chart-title">
                  <span><RiseOutlined style={{ color: 'var(--p5-teal-500)', marginRight: '8px' }} />Diversity Trend</span>
                </div>
                {trend?.data_points?.length > 0 ? (
                  <ReactECharts option={getTrendChart()} style={{ height: 350 }} />
                ) : (
                  <Empty description="No trend data available" />
                )}
              </div>
            </Col>
          </Row>

          {/* Goals Progress */}
          {goals.length > 0 && (
            <div className="p5-card p5-animate-fadeUp" style={{ marginTop: 24, animationDelay: '200ms' }}>
              <div className="p5-card-body">
                <h3 className="p5-section-title"><TrophyOutlined /> FY {currentYear} Goals Progress</h3>
                <Row gutter={[16, 16]}>
                  {goals.slice(0, 4).map(goal => (
                    <Col xs={24} sm={12} lg={6} key={goal.id}>
                      <Card size="small" style={{ background: 'var(--p5-slate-100)' }}>
                        <Statistic
                          title={goal.diversity_type || 'Total Diverse'}
                          value={goal.current_percentage?.toFixed(1)}
                          suffix={`% / ${goal.target_percentage}%`}
                          valueStyle={{
                            color: goal.current_percentage >= goal.target_percentage
                              ? 'var(--p5-emerald-500)'
                              : 'var(--p5-amber-500)'
                          }}
                        />
                        <Progress
                          percent={Math.min((goal.progress_percentage || 0), 100).toFixed(0)}
                          size="small"
                          status={goal.current_percentage >= goal.target_percentage ? 'success' : 'active'}
                        />
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          )}

          {/* Category Breakdown */}
          {dashboard?.by_type?.length > 0 && (
            <div className="p5-card p5-animate-fadeUp" style={{ marginTop: 24, animationDelay: '300ms' }}>
              <div className="p5-card-body">
                <h3 className="p5-section-title"><PercentageOutlined /> Spend by Diversity Category</h3>
                <Row gutter={[16, 16]}>
                  {dashboard.by_type.map(type => (
                    <Col xs={24} sm={12} lg={8} key={type.diversity_type}>
                      <div style={{
                        background: 'var(--p5-slate-100)',
                        borderRadius: 'var(--p5-radius-md)',
                        padding: '16px'
                      }}>
                        <div className="p5-flex-between" style={{ marginBottom: '8px' }}>
                          <span style={{ fontWeight: 500 }}>{type.diversity_type}</span>
                          <Tag color={type.goal_status === 'achieved' ? 'green' : type.goal_status === 'on_track' ? 'blue' : type.goal_status === 'at_risk' ? 'orange' : 'default'}>
                            {type.goal_status?.replace('_', ' ').toUpperCase() || 'NO GOAL'}
                          </Tag>
                        </div>
                        <div className="p5-font-display" style={{ fontSize: '1.5rem', color: 'var(--p5-navy-800)' }}>
                          ${type.total_spend?.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--p5-slate-500)' }}>
                          {type.percentage_of_total?.toFixed(1)}% of total | {type.vendor_count} vendors
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          )}
        </>
      )
    },
    {
      key: 'certifications',
      label: (
        <Badge count={dashboard?.unverified_certifications || 0} offset={[10, 0]}>
          <span className="p5-flex p5-gap-sm">
            <FileDoneOutlined />
            Certifications
          </span>
        </Badge>
      ),
      children: (
        <div className="p5-table-wrapper p5-animate-fadeUp">
          <div className="p5-flex-between" style={{ marginBottom: 16 }}>
            <Space>
              {dashboard?.unverified_certifications > 0 && (
                <Tag color="warning" icon={<ClockCircleOutlined />}>
                  {dashboard.unverified_certifications} Unverified
                </Tag>
              )}
              {dashboard?.certifications_expiring_soon > 0 && (
                <Tag color="orange" icon={<WarningOutlined />}>
                  {dashboard.certifications_expiring_soon} Expiring Soon
                </Tag>
              )}
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCertModalVisible(true)}
            >
              Add Certification
            </Button>
          </div>
          <Table
            dataSource={certifications}
            columns={certColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </div>
      )
    },
    {
      key: 'vendors',
      label: (
        <span className="p5-flex p5-gap-sm">
          <TeamOutlined />
          Diverse Vendors ({dashboard?.diverse_vendor_count || 0})
        </span>
      ),
      children: (
        <div className="p5-table-wrapper p5-animate-fadeUp">
          <Table
            dataSource={diverseVendors}
            columns={vendorColumns}
            rowKey="client_vendor_id"
            pagination={{ pageSize: 10 }}
          />
        </div>
      )
    },
    {
      key: 'goals',
      label: (
        <span className="p5-flex p5-gap-sm">
          <TrophyOutlined />
          Goals
        </span>
      ),
      children: (
        <div className="p5-table-wrapper p5-animate-fadeUp">
          <div className="p5-flex-between" style={{ marginBottom: 16 }}>
            <Space>
              <Tag color="green">{dashboard?.goals_on_track || 0} On Track</Tag>
              <Tag color="orange">{dashboard?.goals_at_risk || 0} At Risk</Tag>
              <Tag color="red">{dashboard?.goals_behind || 0} Behind</Tag>
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setGoalModalVisible(true)}
            >
              Add Goal
            </Button>
          </div>
          <Table
            dataSource={goals}
            columns={goalColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </div>
      )
    }
  ];

  if (error) {
    return (
      <div className="p5-page">
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div className="p5-page">
      {/* Page Header */}
      <div className="p5-page-header p5-flex-between" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 className="p5-page-title">Supplier Diversity</h1>
          <p className="p5-page-subtitle">
            Track diversity certifications, set spend goals, and monitor progress toward supplier diversity targets
          </p>
        </div>
        <Space>
          <Button
            className="p5-btn p5-btn--ghost"
            icon={<ReloadOutlined />}
            onClick={handleRecalculateSpend}
            loading={actionLoading}
          >
            Recalculate
          </Button>
          <Button
            className="p5-btn p5-btn--accent"
            icon={<PlusOutlined />}
            onClick={() => setCertModalVisible(true)}
          >
            Add Certification
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {/* Stats Grid */}
        <div className="p5-stats-grid p5-stagger">
          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--amber">
              <TeamOutlined />
            </div>
            <div className="p5-stat-label">Diverse Vendors</div>
            <div className="p5-stat-value">{dashboard?.diverse_vendor_count || 0}</div>
            <div className="p5-stat-subtext">of {dashboard?.total_vendor_count || 0} total</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--teal">
              <DollarOutlined />
            </div>
            <div className="p5-stat-label">Diverse Spend</div>
            <div className="p5-stat-value">${((dashboard?.diverse_spend || 0) / 1000).toFixed(0)}K</div>
            <div className="p5-stat-subtext">{dashboard?.diverse_percentage?.toFixed(1) || 0}% of total</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--emerald">
              <SafetyCertificateOutlined />
            </div>
            <div className="p5-stat-label">Certifications</div>
            <div className="p5-stat-value">{certifications?.length || 0}</div>
            <div className="p5-stat-subtext">{dashboard?.unverified_certifications || 0} pending verification</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--rose">
              <WarningOutlined />
            </div>
            <div className="p5-stat-label">Expiring Soon</div>
            <div className="p5-stat-value" style={{ color: (dashboard?.certifications_expiring_soon || 0) > 0 ? 'var(--p5-rose-500)' : undefined }}>
              {dashboard?.certifications_expiring_soon || 0}
            </div>
            <div className="p5-stat-subtext">within 90 days</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultActiveKey="overview" items={tabItems} className="p5-tabs" />
      </Spin>

      {/* Add Certification Modal */}
      <Modal
        title="Add Diversity Certification"
        open={certModalVisible}
        onCancel={() => { setCertModalVisible(false); certForm.resetFields(); }}
        footer={null}
        width={600}
      >
        <Form form={certForm} layout="vertical" onFinish={handleCreateCertification}>
          <Form.Item
            name="client_vendor_id"
            label="Vendor"
            rules={[{ required: true, message: 'Select a vendor' }]}
          >
            <Select
              showSearch
              placeholder="Select vendor"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {clientVendors.map(cv => (
                <Option key={cv.id} value={cv.id}>{cv.vendor?.name || 'Unknown Vendor'}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="certification_type"
            label="Certification Type"
            rules={[{ required: true, message: 'Select certification type' }]}
          >
            <Select placeholder="Select type">
              {certificationTypes.map(t => (
                <Option key={t.code} value={t.code}>
                  <Tooltip title={t.description}>{t.code} - {t.name}</Tooltip>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="certifying_agency" label="Certifying Agency">
            <Input placeholder="e.g., NMSDC, WBENC, SBA" />
          </Form.Item>

          <Form.Item name="certificate_number" label="Certificate Number">
            <Input placeholder="Certificate ID/Number" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="certification_date" label="Certification Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expiration_date" label="Expiration Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={actionLoading} block>
              Add Certification
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Goal Modal */}
      <Modal
        title="Add Diversity Goal"
        open={goalModalVisible}
        onCancel={() => { setGoalModalVisible(false); goalForm.resetFields(); }}
        footer={null}
        width={500}
      >
        <Form form={goalForm} layout="vertical" onFinish={handleCreateGoal}>
          <Form.Item
            name="name"
            label="Goal Name"
            rules={[{ required: true, message: 'Enter goal name' }]}
          >
            <Input placeholder="e.g., MBE Spend Target FY2024" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Goal description..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fiscal_year"
                label="Fiscal Year"
                initialValue={currentYear}
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: '100%' }} min={2020} max={2030} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="diversity_type" label="Diversity Type">
                <Select placeholder="Select type (or leave blank for total)" allowClear>
                  {certificationTypes.map(t => (
                    <Option key={t.code} value={t.code}>{t.code}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="target_percentage"
                label="Target %"
                rules={[{ required: true, message: 'Enter target percentage' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} max={100} precision={1} addonAfter="%" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="stretch_percentage" label="Stretch Goal %">
                <InputNumber style={{ width: '100%' }} min={0} max={100} precision={1} addonAfter="%" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={actionLoading} block>
              Create Goal
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierDiversity;
